import React, { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import SyncIcon from "@mui/icons-material/Sync";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useNavigate } from "react-router-dom";
import "./LiveTVPage.scss";
import {
  buildLiveMediaUrl,
  fetchLiveFeed,
  importFreeLiveChannels,
  requestLivePlayback,
} from "../services/liveApi";

const CHROME_TIMEOUT = 3600;

function formatChannelNumber(value) {
  const number = Number(value || 0);
  if (!number) return "00";
  return number.toString().padStart(2, "0");
}

function getChannelKey(channel) {
  return channel?.id || channel?.slug || channel?.name;
}

function LiveTVPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const chromeTimerRef = useRef(null);
  const relayFallbackRef = useRef(false);
  const [feed, setFeed] = useState(null);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [playback, setPlayback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerStatus, setPlayerStatus] = useState("Selectionnez une chaine");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Tous");
  const [importing, setImporting] = useState(false);
  const [showChrome, setShowChrome] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const categories = useMemo(() => ["Tous", ...(feed?.categories || [])], [feed?.categories]);

  const filteredChannels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return channels.filter((channel) => {
      const matchesCategory = category === "Tous" || channel.category === category;
      const haystack = `${channel.name || ""} ${channel.category || ""} ${channel.countryCode || ""} ${channel.languageCode || ""}`.toLowerCase();
      return matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [category, channels, query]);

  const loadFeed = async () => {
    try {
      setLoading(true);
      const data = await fetchLiveFeed();
      setFeed(data);
      setChannels(data?.channels || []);
      setActiveChannel((current) => current || data?.featured?.[0] || data?.channels?.[0] || null);
    } catch (error) {
      console.error("Erreur Direct:", error);
    } finally {
      setLoading(false);
    }
  };

  const scheduleChromeHide = () => {
    setShowChrome(true);
    if (chromeTimerRef.current) {
      window.clearTimeout(chromeTimerRef.current);
    }

    chromeTimerRef.current = window.setTimeout(() => {
      if (!drawerOpen) {
        setShowChrome(false);
      }
    }, CHROME_TIMEOUT);
  };

  useEffect(() => {
    loadFeed();
    scheduleChromeHide();

    return () => {
      if (chromeTimerRef.current) {
        window.clearTimeout(chromeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (drawerOpen) {
      setShowChrome(true);
      return;
    }

    scheduleChromeHide();
  }, [drawerOpen]);

  const startPlayback = async (channel, mode = "direct") => {
    if (!channel) return;

    try {
      relayFallbackRef.current = mode === "relay";
      setActiveChannel(channel);
      setPlayerStatus(mode === "relay" ? "Preparation du relais HLS..." : "Connexion au direct...");
      const data = await requestLivePlayback(getChannelKey(channel), mode);
      setPlayback(data.playback);
    } catch (error) {
      console.error("Erreur lecture Direct:", error);
      setPlayerStatus("Flux indisponible pour le moment");
    }
  };

  useEffect(() => {
    if (!activeChannel || playback) return;
    startPlayback(activeChannel, "direct");
  }, [activeChannel, playback]);

  useEffect(() => {
    const video = videoRef.current;
    const playbackUrl = playback?.url;

    if (!video || !playbackUrl) {
      return undefined;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.volume = volume;
    video.muted = isMuted;
    const src = buildLiveMediaUrl(playbackUrl);

    const attachNative = () => {
      video.src = src;
      video.load();
      video.play().catch(() => setPlayerStatus("Pret a lire"));
    };

    setPlayerStatus(playback.mode === "relay" ? "Direct via relais local" : "Direct");

    if (src.endsWith(".m3u8") && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 3,
      });

      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => setPlayerStatus("Pret a lire"));
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal && playback.mode !== "relay" && !relayFallbackRef.current) {
          relayFallbackRef.current = true;
          startPlayback(activeChannel, "relay");
          return;
        }

        if (data?.fatal) {
          setPlayerStatus("Flux direct indisponible");
        }
      });
    } else {
      attachNative();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [activeChannel, isMuted, playback, volume]);

  const handleImportFreeProvider = async () => {
    try {
      setImporting(true);
      await importFreeLiveChannels({ preset: "europe-africa", limit: 220, perPlaylistLimit: 20 });
      await loadFeed();
      setDrawerOpen(true);
    } catch (error) {
      console.error("Import Direct:", error);
      alert(error.message || "Impossible d'importer les chaines gratuites.");
    } finally {
      setImporting(false);
    }
  };

  const selectChannel = (channel) => {
    setPlayback(null);
    setDrawerOpen(false);
    startPlayback(channel, "direct");
  };

  const changeVolume = (delta) => {
    const nextVolume = Math.min(1, Math.max(0, Number((volume + delta).toFixed(2))));
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0);

    if (videoRef.current) {
      videoRef.current.volume = nextVolume;
      videoRef.current.muted = nextVolume === 0;
    }
  };

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
  };

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      await video.play();
    } else {
      video.pause();
    }
  };

  return (
    <div
      className={`live-tv ${showChrome || drawerOpen ? "live-tv--awake" : "live-tv--idle"}`}
      onMouseMove={scheduleChromeHide}
      onTouchStart={scheduleChromeHide}
    >
      <video
        ref={videoRef}
        className="live-tv__video"
        muted={isMuted}
        playsInline
        poster={buildLiveMediaUrl(activeChannel?.logoUrl)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="live-tv__shade live-tv__shade--top" />
      <div className="live-tv__shade live-tv__shade--bottom" />

      <div className="live-tv__top">
        <button className="live-tv__round" onClick={() => navigate("/home")} aria-label="Retour VOD">
          <ArrowBackIcon />
        </button>
        <div>
          <span className="live-pill">
            <span />
            Direct
          </span>
          <p>{playerStatus}</p>
        </div>
      </div>

      <aside className="live-tv__rail" aria-label="Commandes Direct">
        <button className="live-tv__round" onClick={() => setDrawerOpen(true)} aria-label="Liste des chaines">
          <FormatListBulletedIcon />
        </button>
        <button className="live-tv__round" onClick={() => changeVolume(0.1)} aria-label="Augmenter le volume">
          <VolumeUpIcon />
        </button>
        <button className="live-tv__round" onClick={() => changeVolume(-0.1)} aria-label="Diminuer le volume">
          <VolumeDownIcon />
        </button>
        <button className="live-tv__round" onClick={toggleMute} aria-label="Couper le son">
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </button>
        <button className="live-tv__round" onClick={() => startPlayback(activeChannel, "relay")} aria-label="Relais HLS local">
          <SettingsInputAntennaIcon />
        </button>
        <button className="live-tv__round live-tv__round--creator" onClick={() => navigate("/direct/create")} aria-label="Creer un direct">
          <VideocamOutlinedIcon />
        </button>
      </aside>

      <section className="live-tv__caption">
        <span className="live-channel-number">{formatChannelNumber(activeChannel?.channelNumber)}</span>
        <div>
          <p>{activeChannel?.category || "Chaine"}</p>
          <h1>{activeChannel?.name || "Direct Streamflix"}</h1>
          <span>
            {activeChannel?.languageCode ? activeChannel.languageCode.toUpperCase() : "Langue"} · {activeChannel?.countryCode || "International"} · {activeChannel?.providerName || feed?.provider?.name}
          </span>
        </div>
        {!isPlaying ? (
          <button className="live-tv__play" onClick={togglePlayback} aria-label="Lire le direct">
            <PlayArrowIcon />
          </button>
        ) : null}
      </section>

      <div className={`live-drawer ${drawerOpen ? "is-open" : ""}`}>
        <div className="live-drawer__header">
          <div>
            <p>Guide Direct</p>
            <h2>Chaines Europe et Afrique</h2>
          </div>
          <button className="live-tv__round" onClick={() => setDrawerOpen(false)} aria-label="Fermer">
            <CloseIcon />
          </button>
        </div>

        <div className="live-search">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher une chaine..."
          />
        </div>

        <div className="live-guide">
          <nav className="live-guide__categories" aria-label="Categories">
            {categories.map((item) => (
              <button key={item} className={item === category ? "is-active" : ""} onClick={() => setCategory(item)}>
                {item}
              </button>
            ))}
          </nav>

          <div className="live-guide__channels">
            {loading ? <div className="live-empty">Chargement des chaines...</div> : null}
            {!loading &&
              filteredChannels.map((channel) => (
                <article
                  key={getChannelKey(channel)}
                  className={`live-card ${getChannelKey(activeChannel) === getChannelKey(channel) ? "is-active" : ""}`}
                  onClick={() => selectChannel(channel)}
                >
                  <div className="live-card__number">{formatChannelNumber(channel.channelNumber)}</div>
                  <div className="live-card__logo">
                    <img src={buildLiveMediaUrl(channel.logoUrl)} alt={channel.name} />
                  </div>
                  <div className="live-card__body">
                    <span>{channel.category || "General"}</span>
                    <h3>{channel.name}</h3>
                    <p>
                      {channel.languageCode ? channel.languageCode.toUpperCase() : "Langue"} · {channel.countryCode || "Intl"}
                    </p>
                  </div>
                  <button className="live-card__play" aria-label={`Regarder ${channel.name}`}>
                    <PlayArrowIcon />
                  </button>
                </article>
              ))}

            {!loading && !filteredChannels.length ? (
              <div className="live-empty">
                <LiveTvIcon />
                <p>Aucune chaine ne correspond a ce filtre.</p>
              </div>
            ) : null}
          </div>
        </div>
        
      </div>
    </div>
  );
}

export default LiveTVPage;
